import { useCallback, useRef, useState } from 'react';
import { getCompanySignupConfig } from '../../store/company/apiHelpers';
import { isEmpty } from '../../utils/utils';

export const REGULATORY_RISK_DRAWER_CONFIG_NAME = 'REGULATORY_RISK_DRAWER';

export enum RegulatoryRiskFieldType {
    Text = 'text',
    SourceLink = 'sourceLink',
    ImageLink = 'imageLink',
    MatchedParams = 'matchedParams',
}

export const isHttpUrl = (value: any): value is string => typeof value === 'string' && /^https?:\/\//i.test(value);

// A parameter counts as "matched" when its BE status is a positive match — e.g. "MATCH" or
// "Exact match on gender" — but NOT "NO_MATCH" / "No passport provided" / a mismatch / empty.
export const isMatched = (value: any): boolean => {
    if (isEmpty(value)) return false;
    const u = String(value)
        .toUpperCase()
        .replace(/[\s_-]/g, '');
    return u.includes('MATCH') && !u.includes('NOMATCH') && !u.includes('MISMATCH');
};

// Zigram scores arrive either as a bare number (85) or a percent string ("85%").
// Normalise to a single trailing % so the chip never renders "85%%".
export const formatScore = (value: any): string => {
    if (value === null || value === undefined || value === '') return '';
    return `${String(value).trim().replace(/%\s*$/, '')}%`;
};

// Zigram hit values aren't always strings — some (e.g. Associations) are arrays of objects.
// Coerce any non-primitive to a readable string so the drawer never renders "[object Object]" or crashes.
export const toDisplayString = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value))
        return value
            .map((v) => toDisplayString(v))
            .filter(Boolean)
            .join(', ');
    if (typeof value === 'object') {
        const preferred = value.Name ?? value.name ?? value.value ?? value.description ?? value['Associaton Description'];
        if (preferred !== undefined) return String(preferred);
        return Object.values(value)
            .map((v) => toDisplayString(v))
            .filter(Boolean)
            .join(' — ');
    }
    return String(value);
};

// A single match-status parameter folded into an 'Other Parameters Matched' row.
// rawKey is the BE hit key holding the status; label is the short name shown when it matched.
export interface RegulatoryRiskMatchParam {
    rawKey: string;
    label: string;
}

export interface RegulatoryRiskDrawerField {
    rawKey: string;
    label: string;
    type: RegulatoryRiskFieldType;
    // Only for type 'matchedParams': the candidate params to consolidate. The FE reads each
    // param's value from the hit (BE data) and shows the labels of the ones that matched.
    params?: RegulatoryRiskMatchParam[];
}

export interface RegulatoryRiskDrawerCheckTypeConfig {
    title: string;
    nameMatchKey: string;
    chipLabel?: string;
    fields: RegulatoryRiskDrawerField[];
}

export type RegulatoryRiskDrawerConfig = Record<string, RegulatoryRiskDrawerCheckTypeConfig>;

/**
 * Fetches the PNO-scoped `REGULATORY_RISK_DRAWER` ViewConfig (D4) via the existing
 * `getCompanySignupConfig` helper (genericized to take `configName`) — reuses its
 * `/config/fetch` call + SUCCESS check instead of re-deriving them here.
 * Read path confirmed with the polus slice: PolusResponse.mainConfig is
 * ViewConfig.dataConfig (a MainConfig), and the per-checkType layout map lives at
 * mainConfig.additionalDetailsConfig[checkType] — the same schemaless field
 * AICA_FEATURE_FLAG_CONFIG/AICA_VISIBILITY_CONFIG use.
 */
export const useRegulatoryRiskDrawerConfig = () => {
    const [config, setConfig] = useState<RegulatoryRiskDrawerConfig>({});
    // Last orgId successfully fetched — a switch to a different orgId must refetch.
    const fetchedOrgRef = useRef<string | null>(null);
    // In-flight fetch promise per orgId, so concurrent calls for the same org share one request.
    const inFlightRef = useRef<Record<string, Promise<void> | undefined>>({});

    const ensureConfigLoaded = useCallback(async (investorOrgId?: string) => {
        if (!investorOrgId || fetchedOrgRef.current === investorOrgId) return;
        if (inFlightRef.current[investorOrgId]) return inFlightRef.current[investorOrgId];

        const fetchPromise = Promise.resolve(
            getCompanySignupConfig(
                { orgId: investorOrgId, configName: REGULATORY_RISK_DRAWER_CONFIG_NAME },
                {
                    onSuccess: (data) => {
                        // Only latch "fetched" on success — a failed attempt must retry on next open.
                        fetchedOrgRef.current = investorOrgId;
                        setConfig(data || {});
                    },
                },
            ),
        ).finally(() => {
            delete inFlightRef.current[investorOrgId];
        });

        inFlightRef.current[investorOrgId] = fetchPromise;
        return fetchPromise;
    }, []);

    return { config, ensureConfigLoaded };
};
